/**
 * What the control plane reads inside an instance's cluster, through the
 * diagnostics role, once the customer has opened it (IM-205; the opening is
 * IM-204).
 *
 * ## Only the projection is ever kept
 *
 * A pod as the Kubernetes API returns it carries its containers' environment
 * variables, arguments and annotations — written into the spec literally, so
 * a database password set as an env value is right there. Nothing here holds
 * that object a moment longer than it takes to reduce it. The adapter
 * projects every item before it returns, into the shapes below, which name
 * every field they keep: there is no spread of what the cluster sent, so a
 * field the cluster adds tomorrow is dropped by default rather than carried.
 * `cluster-projection.test.ts` feeds secret-shaped env values, args and
 * annotations through and scans the output with `secretShapedContent`.
 *
 * Free text the cluster wrote — an event's message, a condition's — is the
 * cluster's own words, like a stack event's reason, and goes through
 * `redactText` exactly once, in the projection.
 *
 * ## Bounded twice
 *
 * One list call per kind, at most `limit` items each, and no following of
 * `continue`: the number of requests a run makes is the number of kinds, and
 * `cluster-state.test.ts` in `api` counts them. Then the projected whole is
 * held under `CLUSTER_STATE_LIMITS.maxBytes` by halving the largest kind until
 * it fits. Both cuts are stated on the kind they cut, like the bundle's.
 *
 * ## Unknown is not done
 *
 * A private endpoint and an account that has not opened the cluster are each
 * their own `unreadable` cause with their own sentence, because they send
 * somebody to different places: one is a later phase of this product, the
 * other is a setting on the customer's side. A kind the cluster does not serve
 * — no KEDA installed — is `not-served`, which is an answer, not a failure.
 */
/**
 * The kinds read, and how many of each.
 *
 * Every one is in the ClusterRole IM-204 applies, and `cluster-state.test.ts`
 * holds that true: a kind added here that the role does not grant would be a
 * Forbidden on every account. The versions are what the installer runs today;
 * a cluster serving another version answers 404, which reads as `not-served`
 * naming the version, rather than as a failure to look into.
 *
 * `limit` is what one request asks for. Events are **warnings only**
 * (`selector`), read wide and kept newest first. The API lists events by name,
 * not by time, and a busy cluster holds thousands of `Normal` ones — `Pulled`,
 * `Scheduled`, `Created` — so reading every event and keeping the newest of the
 * first thousand kept neither the newest nor the ones that matter: on a real
 * instance (`es9kp25n`, 2026-10-30) 6 of the 300 kept were warnings. Warnings
 * are what diagnosis reads, and few enough that all of them fit in one request.
 */
export const CLUSTER_KINDS = [
    { id: 'nodes', path: '/api/v1/nodes', limit: 300 },
    { id: 'pods', path: '/api/v1/pods', limit: 500 },
    {
        id: 'events',
        path: '/api/v1/events',
        selector: 'type=Warning',
        limit: 1000,
        keep: 300,
    },
    { id: 'deployments', path: '/apis/apps/v1/deployments', limit: 300 },
    { id: 'statefulSets', path: '/apis/apps/v1/statefulsets', limit: 100 },
    { id: 'daemonSets', path: '/apis/apps/v1/daemonsets', limit: 100 },
    { id: 'jobs', path: '/apis/batch/v1/jobs', limit: 200 },
    {
        id: 'horizontalPodAutoscalers',
        path: '/apis/autoscaling/v2/horizontalpodautoscalers',
        limit: 200,
    },
    { id: 'podDisruptionBudgets', path: '/apis/policy/v1/poddisruptionbudgets', limit: 200 },
    { id: 'persistentVolumeClaims', path: '/api/v1/persistentvolumeclaims', limit: 200 },
    { id: 'nodePools', path: '/apis/karpenter.sh/v1/nodepools', limit: 50 },
    { id: 'nodeClaims', path: '/apis/karpenter.sh/v1/nodeclaims', limit: 300 },
    { id: 'nodeClasses', path: '/apis/karpenter.k8s.aws/v1/ec2nodeclasses', limit: 50 },
    { id: 'scaledObjects', path: '/apis/keda.sh/v1alpha1/scaledobjects', limit: 200 },
    { id: 'scaledJobs', path: '/apis/keda.sh/v1alpha1/scaledjobs', limit: 100 },
    { id: 'clusterQueues', path: '/apis/kueue.x-k8s.io/v1beta1/clusterqueues', limit: 50 },
    { id: 'localQueues', path: '/apis/kueue.x-k8s.io/v1beta1/localqueues', limit: 100 },
    { id: 'workloads', path: '/apis/kueue.x-k8s.io/v1beta1/workloads', limit: 300 },
    { id: 'prometheuses', path: '/apis/monitoring.coreos.com/v1/prometheuses', limit: 20 },
    { id: 'alertmanagers', path: '/apis/monitoring.coreos.com/v1/alertmanagers', limit: 20 },
    {
        id: 'alertmanagerConfigs',
        path: '/apis/monitoring.coreos.com/v1alpha1/alertmanagerconfigs',
        limit: 50,
    },
    { id: 'nodeMetrics', path: '/apis/metrics.k8s.io/v1beta1/nodes', limit: 300 },
    { id: 'podMetrics', path: '/apis/metrics.k8s.io/v1beta1/pods', limit: 500 },
];
/** The list request for one kind: its path, its selector, its limit. */
export const listPathOf = (kind) => `${kind.path}?${kind.selector === undefined ? '' : `fieldSelector=${encodeURIComponent(kind.selector)}&`}limit=${String(kind.limit)}`;
/** The API group and resource a list path reads, as RBAC names them. */
export const groupAndResource = (path) => {
    const parts = path.split('/').filter((p) => p !== '');
    // /api/v1/<resource> is the core group; /apis/<group>/<version>/<resource> any other.
    return parts[0] === 'api'
        ? { group: '', resource: parts[2] ?? '' }
        : { group: parts[1] ?? '', resource: parts[3] ?? '' };
};
export const CLUSTER_STATE_LIMITS = {
    /**
     * The projected whole. About a third of the bundle's five, so the cluster
     * cannot crowd the logs out of it — and what a model is handed later should
     * be well inside one request.
     */
    maxBytes: 1_500_000,
    /**
     * One response, before projection. A list of 500 pods with their managed
     * fields is ten or twenty megabytes; past this the read is abandoned rather
     * than held in a Lambda with 512 MB.
     */
    maxResponseBytes: 32 * 1024 * 1024,
    /** Per request. Inside an API call with a fifteen-second budget. */
    requestTimeoutMs: 8_000,
    /** Free text from the cluster, per field. */
    maxMessageChars: 1_000,
};
/** Said for a cluster whose endpoint this control plane cannot reach. Private clusters are a later phase. */
export const CLUSTER_PRIVATE_REASON = 'the cluster endpoint is private';
/** Said when the cluster refuses the diagnostics role: the customer has not opened it. */
export const CLUSTER_NOT_ENABLED_REASON = 'cluster diagnostics are not enabled for this account — the customer turns them on per region, and the account’s onboarding screen says where they are on';
//# sourceMappingURL=cluster-state.js.map